import { Box, CircularProgress, Dialog, IconButton, Slider, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactPlayer from 'react-player';

interface Video {
    id: number;
    url: string;
    thumbnail: string;
    title: string;
    duration: number;
}

interface VideoGalleryModalProps {
    open: boolean;
    onClose: () => void;
    videos: Video[];
    currentIndex: number;
    onIndexChange: (index: number) => void;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        backgroundColor: theme.palette.mode === 'dark' ? '#242629' : theme.palette.background.paper,
        borderRadius: '12px',
        maxWidth: '95vw',
        maxHeight: '95vh',
        width: '100%',
        height: '100%',
        margin: '20px',
        display: 'flex',
        flexDirection: 'column',
    },
    '& .MuiBackdrop-root': {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
    },
}));

const ModalHeader = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 32px',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.neutralWhite.main,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    width: '40px',
    height: '40px',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
}));

const MainVideoContainer = styled(Box)(() => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    padding: '0 80px 24px 80px',
    minHeight: 0,
}));

const PlayerWrapper = styled(Box)(() => ({
    width: '100%',
    height: '100%',
    maxHeight: '70vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.3)',
    overflow: 'hidden',
    backgroundColor: '#000',
    '& video': {
        borderRadius: '12px',
        width: '100% !important',
        height: '100% !important',
        objectFit: 'contain',
    },
}));

const VideoControlsOverlay = styled(Box)<{ visible: boolean; isPlaying?: boolean }>(({ visible, isPlaying }) => ({
    position: 'absolute',
    bottom: '24px',
    left: '80px',
    right: '80px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(10px)',
    borderRadius: '12px',
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    opacity: visible || !isPlaying ? 1 : 0,
    transition: 'opacity 0.3s ease',
    pointerEvents: visible || !isPlaying ? 'all' : 'none',
}));

const PlayPauseButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.neutralWhite.main,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    width: '48px',
    height: '48px',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
    },
}));

const VideoProgressContainer = styled(Box)(() => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
}));

const TimeDisplay = styled(Typography)(({ theme }) => ({
    color: theme.palette.neutralWhite.main,
    fontSize: '14px',
    fontWeight: 500,
    minWidth: '45px',
}));

const CustomSlider = styled(Slider)(({ theme }) => ({
    color: theme.palette.primary.main,
    height: '6px',
    '& .MuiSlider-track': {
        backgroundColor: theme.palette.primary.main,
        border: 'none',
    },
    '& .MuiSlider-rail': {
        backgroundColor: theme.palette.mode === 'dark' ? '#6B7280' : '#D1D5DB',
        opacity: 0.5,
    },
    '& .MuiSlider-thumb': {
        backgroundColor: theme.palette.neutralWhite.main,
        width: '16px',
        height: '16px',
        '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
            boxShadow: '0 0 0 8px rgba(255, 255, 255, 0.16)',
        },
    },
}));

const VolumeButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.neutralWhite.main,
    width: '40px',
    height: '40px',
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: theme.palette.neutralWhite.main,
    color: theme.palette.neutralBlack.main,
    width: '48px',
    height: '48px',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
    '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? '#E5E7EB' : '#F3F4F6',
    },
    '&:disabled': {
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        color: 'rgba(0, 0, 0, 0.3)',
    },
}));

const PrevButton = styled(NavigationButton)(() => ({
    left: '24px',
}));

const NextButton = styled(NavigationButton)(() => ({
    right: '24px',
}));

const ThumbnailStrip = styled(Box)(() => ({
    display: 'flex',
    overflowX: 'auto',
    gap: '12px',
    padding: '16px 32px 32px 32px',
    '&::-webkit-scrollbar': {
        height: '6px',
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '3px',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
        },
    },
}));

const ThumbnailWrapper = styled(Box)<{ isActive?: boolean }>(({ theme, isActive }) => ({
    minWidth: '120px',
    height: '80px',
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: 'pointer',
    border: isActive ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
    transition: 'all 0.2s ease',
    position: 'relative',
    '&:hover': {
        border: `3px solid ${theme.palette.primary.main}`,
        transform: 'scale(1.05)',
    },
}));

const ThumbnailVideo = styled('video')(() => ({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
}));

const ThumbnailPlayIcon = styled(Box)<{ isActive?: boolean }>(({ theme, isActive }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '24px',
    height: '24px',
    color: theme.palette.neutralWhite.main,
    display: isActive ? 'none' : 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
}));

const VideoCounter = styled(Typography)(({ theme }) => ({
    color: theme.palette.neutralWhite.main,
    fontSize: '16px',
    fontWeight: 500,
}));

const LoadingContainer = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '400px',
}));

const CenterPlayButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: theme.palette.neutralBlack.main,
    width: '80px',
    height: '80px',
    fontSize: '32px',
    '&:hover': {
        backgroundColor: theme.palette.neutralWhite.main,
    },
}));

const VideoGalleryModal: React.FC<VideoGalleryModalProps> = ({
    open,
    onClose,
    videos,
    currentIndex,
    onIndexChange,
}) => {
    const theme = useTheme();
    const playerRef = useRef<ReactPlayer>(null);
    const thumbnailStripRef = useRef<HTMLDivElement>(null);
    const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);
    const controlsTimeoutRef = useRef<NodeJS.Timeout>();

    // Video state
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    
    // UI state
    const [isMuted, setIsMuted] = useState(false);
    const [controlsVisible, setControlsVisible] = useState(true);
    const [seeking, setSeeking] = useState(false);

    const currentVideo = videos[currentIndex];

    // Format time helper
    const formatTime = (seconds: number): string => {
        if (!isFinite(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Show controls and auto-hide after 3 seconds
    const showControls = useCallback(() => {
        setControlsVisible(true);
        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
        }
        if (isPlaying) {
            controlsTimeoutRef.current = setTimeout(() => {
                setControlsVisible(false);
            }, 3000);
        }
    }, [isPlaying]);

    // Play/Pause toggle
    const togglePlayPause = useCallback(() => {
        setIsPlaying(prev => !prev);
    }, []);

    // Navigation
    const goToPrevious = useCallback(() => {
        if (currentIndex > 0) {
            onIndexChange(currentIndex - 1);
        }
    }, [currentIndex, onIndexChange]);

    const goToNext = useCallback(() => {
        if (currentIndex < videos.length - 1) {
            onIndexChange(currentIndex + 1);
        }
    }, [currentIndex, videos.length, onIndexChange]);

    // Progress bar handlers
    const handleSeek = useCallback((event: Event, value: number | number[]) => {
        if (typeof value !== 'number') return;
        setSeeking(true);
        const time = (value / 100) * duration;
        setCurrentTime(time);
    }, [duration]);

    const handleSeekCommitted = useCallback((event: Event | React.SyntheticEvent, value: number | number[]) => {
        if (!playerRef.current || typeof value !== 'number') return;
        const time = (value / 100) * duration;
        playerRef.current.seekTo(time, 'seconds');
        setSeeking(false);
    }, [duration]);

    // Volume toggle
    const toggleMute = useCallback(() => {
        setIsMuted(prev => !prev);
    }, []);

    // Keyboard controls
    useEffect(() => {
        if (!open) return;

        const handleKeyPress = (e: KeyboardEvent) => {
            switch (e.key) {
                case ' ':
                    e.preventDefault();
                    togglePlayPause();
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    goToPrevious();
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    goToNext();
                    break;
                case 'Escape':
                    e.preventDefault();
                    onClose();
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [open, togglePlayPause, goToPrevious, goToNext, onClose]);

    // Player callbacks
    const handleProgress = useCallback((state: { played: number; playedSeconds: number; loaded: number; loadedSeconds: number }) => {
        if (!seeking) {
            setCurrentTime(state.playedSeconds);
        }
    }, [seeking]);

    const handleDuration = useCallback((duration: number) => {
        setDuration(duration);
        setIsLoading(false);
    }, []);

    const handleEnded = useCallback(() => {
        setIsPlaying(false);
        if (currentIndex < videos.length - 1) {
            onIndexChange(currentIndex + 1);
        }
    }, [currentIndex, videos.length, onIndexChange]);

    const handleReady = useCallback(() => {
        setIsLoading(false);
    }, []);

    // Reset video state when switching videos
    useEffect(() => {
        setCurrentTime(0);
        setDuration(0);
        setIsPlaying(false);
        setIsLoading(true);
        setSeeking(false);
    }, [currentIndex]);

    // Auto-scroll to active thumbnail
    useEffect(() => {
        if (!open || !thumbnailStripRef.current || !thumbnailRefs.current[currentIndex]) return;

        const container = thumbnailStripRef.current;
        const thumbnail = thumbnailRefs.current[currentIndex];
        
        if (container && thumbnail) {
            const scrollLeft = thumbnail.offsetLeft - (container.clientWidth / 2) + (thumbnail.offsetWidth / 2);
            container.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
        }
    }, [currentIndex, open]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
        };
    }, []);

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
        <StyledDialog open={open} onClose={onClose} maxWidth={false} fullWidth>
            <ModalHeader>
                <VideoCounter>
                    {currentIndex + 1} / {videos.length}
                </VideoCounter>
                <CloseButton onClick={onClose}>
                    <i className="fi fi-br-cross" style={{ fontSize: '20px' }} />
                </CloseButton>
            </ModalHeader>

            <MainVideoContainer onMouseMove={showControls}>
                {isLoading && (
                    <LoadingContainer>
                        <CircularProgress size={60} sx={{ color: theme.palette.neutralWhite.main }} />
                    </LoadingContainer>
                )}

                <PlayerWrapper sx={{ display: isLoading ? 'none' : 'flex' }}>
                    <ReactPlayer
                        ref={playerRef}
                        url={currentVideo?.url}
                        playing={isPlaying}
                        muted={isMuted}
                        width="100%"
                        height="100%"
                        onProgress={handleProgress}
                        onDuration={handleDuration}
                        onEnded={handleEnded}
                        onReady={handleReady}
                        progressInterval={100}
                        config={{
                            file: {
                                attributes: {
                                    style: { 
                                        width: '100%', 
                                        height: '100%',
                                        objectFit: 'contain'
                                    }
                                }
                            }
                        }}
                    />
                </PlayerWrapper>

                {/* Center Play Button - shown when paused */}
                {!isPlaying && !isLoading && (
                    <CenterPlayButton onClick={togglePlayPause}>
                        <i className="fi fi-sr-play" style={{ fontSize: '40px' }} />
                    </CenterPlayButton>
                )}

                {/* Video Controls Overlay */}
                <VideoControlsOverlay visible={controlsVisible} isPlaying={isPlaying}>
                    <PlayPauseButton onClick={togglePlayPause}>
                        <i 
                            className={isPlaying ? 'fi fi-sr-pause' : 'fi fi-sr-play'} 
                            style={{ fontSize: '24px' }} 
                        />
                    </PlayPauseButton>

                    <VideoProgressContainer>
                        <TimeDisplay>{formatTime(currentTime)}</TimeDisplay>
                        <CustomSlider
                            value={progress}
                            onChange={handleSeek}
                            onChangeCommitted={handleSeekCommitted}
                            min={0}
                            max={100}
                            step={0.1}
                        />
                        <TimeDisplay>{formatTime(duration)}</TimeDisplay>
                    </VideoProgressContainer>

                    <VolumeButton onClick={toggleMute}>
                        <i 
                            className={isMuted ? 'fi fi-sr-volume-mute' : 'fi fi-sr-volume'} 
                            style={{ fontSize: '20px' }} 
                        />
                    </VolumeButton>
                </VideoControlsOverlay>

                {/* Navigation Buttons */}
                <PrevButton onClick={goToPrevious} disabled={currentIndex === 0}>
                    <i className="fi fi-br-angle-left" style={{ fontSize: '24px' }} />
                </PrevButton>

                <NextButton onClick={goToNext} disabled={currentIndex === videos.length - 1}>
                    <i className="fi fi-br-angle-right" style={{ fontSize: '24px' }} />
                </NextButton>
            </MainVideoContainer>

            {/* Thumbnail Strip */}
            <ThumbnailStrip ref={thumbnailStripRef}>
                {videos.map((video, index) => (
                    <ThumbnailWrapper
                        key={video.id}
                        ref={(el: HTMLDivElement | null) => { thumbnailRefs.current[index] = el; }}
                        isActive={index === currentIndex}
                        onClick={() => onIndexChange(index)}
                    >
                        <ThumbnailVideo
                            src={video.url}
                            preload="metadata"
                            muted
                            playsInline
                        />
                        <ThumbnailPlayIcon isActive={index === currentIndex}>
                            <i className="fi fi-sr-play" style={{ fontSize: '20px' }} />
                        </ThumbnailPlayIcon>
                    </ThumbnailWrapper>
                ))}
            </ThumbnailStrip>
        </StyledDialog>
    );
};

export default VideoGalleryModal;
